'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { applyGraphEnvOverride } from './graph-env-override';
import {
  applyPlayerTextureDownscale,
  getTextureQualityCacheSuffix,
  optimizeTextureUrl,
  registerPlayerTextureCacheClearHook,
} from './player-texture-quality';
import { configureStableTextureSampling } from './texture-sampling';
import { applyTransmitBlurDefine } from './transmission-blur-patch';
import { graphTextureReadyForGpu, promotePendingCpuProcessTexture } from './material-graph-eval';

const LINEAR_TEX_SLOTS = new Set([
  'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
  'aoMap', 'displacementMap', 'alphaMap', 'anisotropyMap',
  'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
]);

const textureLoader = new THREE.TextureLoader();
const textureCache = new Map<string, THREE.Texture>();

registerPlayerTextureCacheClearHook(() => {
  for (const tex of textureCache.values()) {
    tex.dispose();
  }
  textureCache.clear();
});

function loadTexture(url: string, linear = false): THREE.Texture {
  const cacheKey = `${url}__${linear ? 'L' : 'S'}__${getTextureQualityCacheSuffix()}`;
  if (textureCache.has(cacheKey)) return textureCache.get(cacheKey)!;
  const fetchUrl = optimizeTextureUrl(url);
  const tex = textureLoader.load(
    fetchUrl,
    (t) => {
      applyPlayerTextureDownscale(t);
      configureStableTextureSampling(t);
    },
    undefined,
    () => {
      textureCache.delete(cacheKey);
    },
  );
  tex.colorSpace = linear ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
  tex.flipY = false;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  textureCache.set(cacheKey, tex);
  return tex;
}

let _uvCheckerTex: THREE.CanvasTexture | null = null;
function getUvChecker(): THREE.CanvasTexture {
  if (_uvCheckerTex) return _uvCheckerTex;
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cells = 8;
  const cellSize = size / cells;
  const colors = [
    '#e6194B', '#3cb44b', '#ffe119', '#4363d8',
    '#f58231', '#911eb4', '#42d4f4', '#f032e6',
    '#bfef45', '#fabed4', '#469990', '#dcbeff',
    '#9A6324', '#800000', '#aaffc3', '#000075',
  ];
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? colors[(x + y * cells) % colors.length] : '#ffffff';
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  _uvCheckerTex = tex;
  return tex;
}

function safeColorSet(target: THREE.Color, value: any) {
  try {
    if (typeof value === 'string') target.set(value);
    else if (value instanceof THREE.Color) target.copy(value);
  } catch { /* ignore */ }
}

export type PrimitiveDisplayMode = 'default' | 'matteWhite' | 'glossyGray' | 'normals' | 'uvChecker' | 'graphOverride';

export interface PrimitiveMaterialProps {
  overrides?: Record<string, any> | null;
  fallback?: {
    color?: string;
    metalness?: number;
    roughness?: number;
    opacity?: number;
  };
  displayMode?: PrimitiveDisplayMode;
  depthOffset?: number;
  stayOnTop?: boolean;
}

const TEX_SLOTS = [
  'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
  'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
  'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
] as const;

function applyOverrides(
  mat: THREE.MeshPhysicalMaterial,
  ov: Record<string, any> | null | undefined,
  fb: PrimitiveMaterialProps['fallback'],
): Map<string, THREE.Texture> | null {
  let pending: Map<string, THREE.Texture> | null = null;
  mat.color.set(ov?.color ?? fb?.color ?? '#cccccc');
  mat.metalness = ov?.metalness ?? fb?.metalness ?? 0;
  mat.roughness = ov?.roughness ?? fb?.roughness ?? 0.5;
  mat.toneMapped = ov?.skipToneMap !== true;

  if (ov?.emissive) safeColorSet(mat.emissive, ov.emissive);
  else mat.emissive.set(0x000000);
  mat.emissiveIntensity = ov?.emissiveIntensity ?? 1;

  mat.clearcoat = ov?.clearcoat ?? 0;
  mat.clearcoatRoughness = ov?.clearcoatRoughness ?? 0;
  mat.transmission = ov?.transmission ?? 0;
  mat.ior = ov?.ior ?? 1.5;
  mat.thickness = ov?.thickness ?? 0;
  mat.specularIntensity = ov?.specularIntensity ?? 1;
  if (ov?.specularColor) safeColorSet(mat.specularColor, ov.specularColor);
  mat.envMapIntensity = ov?.envMapIntensity ?? 1;
  if (ov?.sheenColor) safeColorSet(mat.sheenColor, ov.sheenColor);
  mat.sheenRoughness = ov?.sheenRoughness ?? 0;
  mat.anisotropy = ov?.anisotropy ?? 0;
  mat.anisotropyRotation = ov?.anisotropyRotation ?? 0;
  if (ov?.bumpScale != null) mat.bumpScale = ov.bumpScale;
  if (ov?.normalScale != null) {
    const ns = ov.normalScale as number;
    mat.normalScale.set(ns, ns);
  }

  for (const slot of TEX_SLOTS) {
    const val = ov?.[slot];
    let tex: THREE.Texture | null = null;
    if (val instanceof THREE.Texture) {
      tex = val;
      promotePendingCpuProcessTexture(tex);
    } else if (typeof val === 'string' && val) {
      tex = loadTexture(val, LINEAR_TEX_SLOTS.has(slot));
    }
    if (tex && graphTextureReadyForGpu(tex)) {
      if (slot === 'map' || slot === 'emissiveMap') tex.colorSpace = THREE.SRGBColorSpace;
      else if (LINEAR_TEX_SLOTS.has(slot)) tex.colorSpace = THREE.LinearSRGBColorSpace;
      (mat as any)[slot] = tex;
    } else if (tex) {
      // Texture not loaded yet — defer so the shader doesn't sample
      // an uninitialised (black) texture. Polling will assign later.
      if (!pending) pending = new Map();
      pending.set(slot, tex);
      (mat as any)[slot] = null;
    } else {
      (mat as any)[slot] = null;
    }
  }

  if (mat.anisotropyMap && mat.anisotropy <= 0) mat.anisotropy = 1;

  const opacity = ov?.opacity ?? fb?.opacity ?? 1;
  mat.opacity = opacity;

  const hasAlphaMap = mat.alphaMap != null;
  const hasTransmission = mat.transmission > 0;
  const needsAlpha = opacity < 1 || hasAlphaMap;
  mat.transparent = needsAlpha && !hasTransmission;

  if (needsAlpha && !hasAlphaMap && !hasTransmission) {
    mat.depthWrite = false;
    const opVal = opacity;
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.u_opacityOverride = { value: opVal };
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        '#include <common>\nuniform float u_opacityOverride;',
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        'gl_FragColor.a *= u_opacityOverride;\n#include <dithering_fragment>',
      );
    };
    mat.customProgramCacheKey = () => `prim_opacity_${opVal}`;
  } else if (hasTransmission) {
    mat.depthWrite = true;
    applyTransmitBlurDefine(mat, ov?.transmitBlur ?? 0);
    mat.onBeforeCompile = () => {};
    mat.customProgramCacheKey = () => '';
  } else {
    applyTransmitBlurDefine(mat, 0);
    mat.depthWrite = hasAlphaMap ? true : !needsAlpha;
    mat.onBeforeCompile = () => {};
    mat.customProgramCacheKey = () => '';
  }

  if (ov?._falloff) {
    const { front, side, power } = ov._falloff;
    const uFront = { value: new THREE.Color(front) };
    const uSide = { value: new THREE.Color(side) };
    const uPow = { value: power };
    const prevObc = mat.onBeforeCompile;
    mat.onBeforeCompile = (shader, renderer) => {
      if (prevObc) prevObc(shader, renderer);
      shader.uniforms.u_falloffFront = uFront;
      shader.uniforms.u_falloffSide = uSide;
      shader.uniforms.u_falloffPow = uPow;
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
{
  float falloffNdotV = max(dot(normal, normalize(vViewPosition)), 0.0);
  float falloffFactor = pow(1.0 - falloffNdotV, u_falloffPow);
  vec3 _foBase = mix(u_falloffFront, u_falloffSide, falloffFactor);
#ifdef USE_MAP
  vec4 _foMap = texture2D(map, vMapUv);
  diffuseColor.rgb = _foBase * _foMap.rgb;
#else
  diffuseColor.rgb = _foBase;
#endif
}`,
      );
      shader.fragmentShader =
        'uniform vec3 u_falloffFront;\nuniform vec3 u_falloffSide;\nuniform float u_falloffPow;\n' +
        shader.fragmentShader;
    };
    mat.customProgramCacheKey = () => `falloff_${mat.uuid}`;
  }

  // Rounded edge for primitives is handled by the parent mesh component
  // which has access to the geometry for smoothed-normal attribute injection.

  if (ov?._invertAlpha) {
    const prevObcAlpha = mat.onBeforeCompile;
    mat.onBeforeCompile = (shader, renderer) => {
      if (prevObcAlpha) prevObcAlpha(shader, renderer);
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <alphamap_fragment>',
        [
          '#ifdef USE_ALPHAMAP',
          '\tdiffuseColor.a *= ( 1.0 - texture2D( alphaMap, vAlphaMapUv ).g );',
          '#endif',
        ].join('\n'),
      );
    };
    const prevKey = mat.customProgramCacheKey?.() || '';
    mat.customProgramCacheKey = () => prevKey + '|invertAlpha';
  }

  if (ov?._materialLayers) {
    const layers = ov._materialLayers as Array<{
      mask: THREE.Texture;
      maskUvChannel: number;
      color: string;
      map: THREE.Texture | null;
      mapUvChannel?: number;
      normalMap: THREE.Texture | null;
      normalUvChannel?: number;
      roughness: number;
      roughnessMap: THREE.Texture | null;
      roughnessUvChannel?: number;
      metalness: number;
      metalnessMap: THREE.Texture | null;
      metalnessUvChannel?: number;
      amount: number;
    }>;
    const count = Math.min(layers.length, 3);

    type TexSlot = { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
    type LayerUniforms = {
      mask: TexSlot; color: { value: THREE.Color }; map: TexSlot; normalMap: TexSlot;
      roughness: { value: number }; roughnessMap: TexSlot;
      metalness: { value: number }; metalnessMap: TexSlot;
      amount: { value: number };
    };
    const mkSlot = (): TexSlot => ({ tex: { value: null }, uvMat: { value: new THREE.Matrix3() } });
    let luArr = (mat.userData as any)._layerUniforms as LayerUniforms[] | undefined;
    if (!luArr || luArr.length < count) {
      luArr = [];
      for (let i = 0; i < count; i++) {
        luArr.push({
          mask: mkSlot(), color: { value: new THREE.Color('#ffffff') },
          map: mkSlot(), normalMap: mkSlot(),
          roughness: { value: 0.5 }, roughnessMap: mkSlot(),
          metalness: { value: 0 }, metalnessMap: mkSlot(),
          amount: { value: 1 },
        });
      }
      (mat.userData as any)._layerUniforms = luArr;
    }

    const syncTex = (slot: TexSlot, tex: THREE.Texture | null) => {
      slot.tex.value = tex;
      if (tex) { tex.updateMatrix(); slot.uvMat.value.copy(tex.matrix); }
      else { slot.uvMat.value.identity(); }
    };

    const flags: Array<{
      hasMap: boolean; hasNormal: boolean; hasRoughMap: boolean; hasMetalMap: boolean;
      maskUvCh: number; mapUvCh: number; normalUvCh: number; roughUvCh: number; metalUvCh: number;
    }> = [];
    for (let i = 0; i < count; i++) {
      const l = layers[i];
      syncTex(luArr[i].mask, l.mask);
      try { luArr[i].color.value.set(l.color); } catch { luArr[i].color.value.set('#ffffff'); }
      syncTex(luArr[i].map, l.map);
      syncTex(luArr[i].normalMap, l.normalMap);
      luArr[i].roughness.value = l.roughness;
      syncTex(luArr[i].roughnessMap, l.roughnessMap);
      luArr[i].metalness.value = l.metalness;
      syncTex(luArr[i].metalnessMap, l.metalnessMap);
      luArr[i].amount.value = l.amount;
      flags.push({
        hasMap: !!l.map, hasNormal: !!l.normalMap,
        hasRoughMap: !!l.roughnessMap, hasMetalMap: !!l.metalnessMap,
        maskUvCh: l.maskUvChannel ?? 0,
        mapUvCh: l.mapUvChannel ?? 0,
        normalUvCh: l.normalUvChannel ?? 0,
        roughUvCh: l.roughnessUvChannel ?? 0,
        metalUvCh: l.metalnessUvChannel ?? 0,
      });
    }

    const layerUniforms = luArr.slice(0, count);
    const layerFlags = [...flags];

    const matUvChannels = new Set<number>();
    for (const prop of ['map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
      'aoMap', 'alphaMap', 'emissiveMap', 'displacementMap', 'clearcoatMap',
      'clearcoatRoughnessMap', 'clearcoatNormalMap', 'anisotropyMap']) {
      const tex = (mat as any)[prop];
      if (tex instanceof THREE.Texture) matUvChannels.add(tex.channel ?? 0);
    }

    const prevObcLayers = mat.onBeforeCompile;
    mat.onBeforeCompile = (shader, renderer) => {
      if (prevObcLayers) prevObcLayers(shader, renderer);

      let vertVaryings = 'varying vec2 v_layerUv;\n';
      let vertAssigns = 'v_layerUv = uv;\n';
      let fragVaryings = 'varying vec2 v_layerUv;\n';
      const declaredAttrs = new Set<string>();
      const layerVarying = (i: number, slot: 'mask' | 'map' | 'norm' | 'rough' | 'metal') => {
        if (slot === 'mask') return `v_layer${i}_maskUv`;
        if (slot === 'map') return `v_layer${i}_mapUv`;
        if (slot === 'norm') return `v_layer${i}_normUv`;
        if (slot === 'rough') return `v_layer${i}_roughUv`;
        return `v_layer${i}_metalUv`;
      };
      type UvNeed = { i: number; slot: 'mask' | 'map' | 'norm' | 'rough' | 'metal'; ch: number };
      const uvNeeds: UvNeed[] = [];
      for (let i = 0; i < count; i++) {
        const f = layerFlags[i];
        if (f.maskUvCh > 0) uvNeeds.push({ i, slot: 'mask', ch: f.maskUvCh });
        if (f.hasMap && f.mapUvCh > 0) uvNeeds.push({ i, slot: 'map', ch: f.mapUvCh });
        if (f.hasNormal && f.normalUvCh > 0) uvNeeds.push({ i, slot: 'norm', ch: f.normalUvCh });
        if (f.hasRoughMap && f.roughUvCh > 0) uvNeeds.push({ i, slot: 'rough', ch: f.roughUvCh });
        if (f.hasMetalMap && f.metalUvCh > 0) uvNeeds.push({ i, slot: 'metal', ch: f.metalUvCh });
      }
      for (const need of uvNeeds) {
        const attr = `uv${need.ch}`;
        const alreadyInShader = shader.vertexShader.includes(`attribute vec2 ${attr}`) || shader.vertexShader.includes(`vec2 ${attr};`);
        if (!declaredAttrs.has(attr) && !matUvChannels.has(need.ch) && !alreadyInShader) {
          vertVaryings += `attribute vec2 ${attr};\n`;
          declaredAttrs.add(attr);
        }
        const vn = layerVarying(need.i, need.slot);
        vertVaryings += `varying vec2 ${vn};\n`;
        vertAssigns += `${vn} = ${attr};\n`;
        fragVaryings += `varying vec2 ${vn};\n`;
      }

      shader.vertexShader = vertVaryings + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        '#include <uv_vertex>',
        '#include <uv_vertex>\n' + vertAssigns,
      );

      let uniformDecl = fragVaryings
        + 'vec2 _txUv(mat3 m, vec2 uv) { return (m * vec3(uv, 1.0)).xy; }\n';
      let blendCode = '';
      let normalBlendCode = '';
      for (let i = 0; i < count; i++) {
        const u = layerUniforms[i];
        const f = layerFlags[i];
        shader.uniforms[`u_layer${i}_mask`] = u.mask.tex;
        shader.uniforms[`u_layer${i}_maskMat`] = u.mask.uvMat;
        shader.uniforms[`u_layer${i}_color`] = u.color;
        shader.uniforms[`u_layer${i}_rough`] = u.roughness;
        shader.uniforms[`u_layer${i}_metal`] = u.metalness;
        shader.uniforms[`u_layer${i}_amount`] = u.amount;

        uniformDecl += `uniform sampler2D u_layer${i}_mask;\n`
          + `uniform mat3 u_layer${i}_maskMat;\n`
          + `uniform vec3 u_layer${i}_color;\n`
          + `uniform float u_layer${i}_rough;\n`
          + `uniform float u_layer${i}_metal;\n`
          + `uniform float u_layer${i}_amount;\n`;

        if (f.hasMap) {
          shader.uniforms[`u_layer${i}_map`] = u.map.tex;
          shader.uniforms[`u_layer${i}_mapMat`] = u.map.uvMat;
          uniformDecl += `uniform sampler2D u_layer${i}_map;\nuniform mat3 u_layer${i}_mapMat;\n`;
        }
        if (f.hasNormal) {
          shader.uniforms[`u_layer${i}_normalMap`] = u.normalMap.tex;
          shader.uniforms[`u_layer${i}_normalMapMat`] = u.normalMap.uvMat;
          uniformDecl += `uniform sampler2D u_layer${i}_normalMap;\nuniform mat3 u_layer${i}_normalMapMat;\n`;
        }
        if (f.hasRoughMap) {
          shader.uniforms[`u_layer${i}_roughMap`] = u.roughnessMap.tex;
          shader.uniforms[`u_layer${i}_roughMapMat`] = u.roughnessMap.uvMat;
          uniformDecl += `uniform sampler2D u_layer${i}_roughMap;\nuniform mat3 u_layer${i}_roughMapMat;\n`;
        }
        if (f.hasMetalMap) {
          shader.uniforms[`u_layer${i}_metalMap`] = u.metalnessMap.tex;
          shader.uniforms[`u_layer${i}_metalMapMat`] = u.metalnessMap.uvMat;
          uniformDecl += `uniform sampler2D u_layer${i}_metalMap;\nuniform mat3 u_layer${i}_metalMapMat;\n`;
        }

        const maskUvVar = f.maskUvCh > 0 ? `v_layer${i}_maskUv` : 'v_layerUv';
        const normUvVar = f.normalUvCh > 0 ? `v_layer${i}_normUv` : 'v_layerUv';
        const maskUv = `_txUv(u_layer${i}_maskMat, ${maskUvVar})`;
        const colorExpr = f.hasMap
          ? `texture2D(u_layer${i}_map, _txUv(u_layer${i}_mapMat, v_layerUv)).rgb`
          : `u_layer${i}_color`;
        const roughExpr = f.hasRoughMap
          ? `texture2D(u_layer${i}_roughMap, _txUv(u_layer${i}_roughMapMat, v_layerUv)).g`
          : `u_layer${i}_rough`;
        const metalExpr = f.hasMetalMap
          ? `texture2D(u_layer${i}_metalMap, _txUv(u_layer${i}_metalMapMat, v_layerUv)).b`
          : `u_layer${i}_metal`;

        blendCode += `{\n`
          + `  float lm${i} = texture2D(u_layer${i}_mask, ${maskUv}).r * u_layer${i}_amount;\n`
          + `  diffuseColor.rgb = mix(diffuseColor.rgb, ${colorExpr}, lm${i});\n`
          + `  roughnessFactor = mix(roughnessFactor, ${roughExpr}, lm${i});\n`
          + `  metalnessFactor = mix(metalnessFactor, ${metalExpr}, lm${i});\n`
          + `}\n`;

        if (f.hasNormal) {
          normalBlendCode += `{\n`
            + `  float lm${i} = texture2D(u_layer${i}_mask, ${maskUv}).r * u_layer${i}_amount;\n`
            + `  vec3 _ln${i} = texture2D(u_layer${i}_normalMap, _txUv(u_layer${i}_normalMapMat, ${normUvVar})).xyz * 2.0 - 1.0;\n`
            + `  normal = normalize(mix(normal, _ln${i}, lm${i}));\n`
            + `}\n`;
        } else {
          normalBlendCode += `{\n`
            + `  float lm${i} = texture2D(u_layer${i}_mask, ${maskUv}).r * u_layer${i}_amount;\n`
            + `  normal = normalize(mix(normal, normalize(vNormal) * faceDirection, lm${i}));\n`
            + `}\n`;
        }
      }

      shader.fragmentShader = uniformDecl + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <metalnessmap_fragment>',
        '#include <metalnessmap_fragment>\n' + blendCode,
      );
      if (normalBlendCode) {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <normal_fragment_maps>',
          '#include <normal_fragment_maps>\n' + normalBlendCode,
        );
      }
    };
    const flagKey = layerFlags.map((f, i) =>
      `${i}${f.hasMap ? 'm' : ''}${f.hasNormal ? 'n' : ''}${f.hasRoughMap ? 'r' : ''}${f.hasMetalMap ? 'e' : ''}`
      + `M${f.maskUvCh}P${f.mapUvCh}N${f.normalUvCh}R${f.roughUvCh}E${f.metalUvCh}`
    ).join('_');
    const prevKey = mat.customProgramCacheKey?.() || '';
    mat.customProgramCacheKey = () => prevKey + `|layers${count}_${flagKey}`;
  }

  if (applyGraphEnvOverride(mat, ov ?? {}, null)) {
    mat.needsUpdate = true;
  }

  mat.needsUpdate = true;
  return pending;
}

export function PrimitiveMaterial({ overrides, fallback, displayMode = 'default', depthOffset = 0, stayOnTop = false }: PrimitiveMaterialProps) {
  const matRef = useRef<THREE.MeshPhysicalMaterial>(null!);
  const ovRef = useRef(overrides);
  const fbRef = useRef(fallback);
  ovRef.current = overrides;
  fbRef.current = fallback;

  const mat = useMemo(() => {
    const m = new THREE.MeshPhysicalMaterial();
    return m;
  }, []);

  const displayMatRef = useRef<THREE.Material | null>(null);

  const pendingRef = useRef<Map<string, THREE.Texture> | null>(null);

  useEffect(() => {
    if (displayMatRef.current) {
      displayMatRef.current.dispose();
      displayMatRef.current = null;
    }

    if (displayMode !== 'default' && displayMode !== 'graphOverride') {
      switch (displayMode) {
        case 'matteWhite':
          displayMatRef.current = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0 });
          break;
        case 'glossyGray':
          displayMatRef.current = new THREE.MeshPhysicalMaterial({ color: 0x999999, roughness: 0.08, metalness: 0.6, clearcoat: 0.4, clearcoatRoughness: 0.05 });
          break;
        case 'normals':
          displayMatRef.current = new THREE.MeshNormalMaterial();
          break;
        case 'uvChecker':
          displayMatRef.current = new THREE.MeshStandardMaterial({ map: getUvChecker(), roughness: 1, metalness: 0 });
          break;
      }
    } else {
      mat.wireframe = false;
    }

    return () => {
      if (displayMatRef.current) {
        displayMatRef.current.dispose();
        displayMatRef.current = null;
      }
      mat.wireframe = false;
    };
  }, [displayMode, mat]);

  useEffect(() => {
    if (!mat || (displayMode !== 'default' && displayMode !== 'graphOverride')) return;
    pendingRef.current = applyOverrides(mat, overrides, fallback);
  }, [mat, overrides, fallback, displayMode]);

  useEffect(() => {
    if (depthOffset !== 0) {
      mat.polygonOffset = true;
      mat.polygonOffsetFactor = -depthOffset;
      mat.polygonOffsetUnits = -depthOffset;
    } else {
      mat.polygonOffset = false;
      mat.polygonOffsetFactor = 0;
      mat.polygonOffsetUnits = 0;
    }
    mat.needsUpdate = true;
  }, [mat, depthOffset]);

  useEffect(() => {
    const active = displayMatRef.current ?? mat;
    active.depthTest = true;
    active.needsUpdate = true;
  }, [displayMode, mat, stayOnTop]);

  useEffect(() => {
    return () => { mat.dispose(); };
  }, [mat]);

  useFrame(() => {
    if (displayMode !== 'default' && displayMode !== 'graphOverride') return;
    const deferred = pendingRef.current;
    if (!deferred || deferred.size === 0) return;
    let anyAssigned = false;
    for (const [slot, tex] of Array.from(deferred.entries())) {
      promotePendingCpuProcessTexture(tex);
      if (graphTextureReadyForGpu(tex)) {
        if (slot === 'map' || slot === 'emissiveMap') tex.colorSpace = THREE.SRGBColorSpace;
        else if (LINEAR_TEX_SLOTS.has(slot)) tex.colorSpace = THREE.LinearSRGBColorSpace;
        (mat as any)[slot] = tex;
        deferred.delete(slot);
        anyAssigned = true;
      }
    }
    if (anyAssigned && deferred.size === 0) {
      pendingRef.current = null;
      if (mat.alphaMap) {
        mat.transparent = true;
        mat.depthWrite = true;
      }
      mat.needsUpdate = true;
    }
  });

  const activeMat = displayMode !== 'default' && displayMatRef.current
    ? displayMatRef.current
    : mat;

  return <primitive ref={matRef} object={activeMat} attach="material" />;
}
